import { HTMLAttributes, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Box, TextField } from '@mui/material';
import { AuditLogsAccessResponse } from 'api';
import { CHILDREN_BUSINESS_UNIT, FIRM_KIND, STANDALONE_KIND } from 'pages/AuditLogs/constants';
import { FlatOption } from 'pages/AuditLogs/types';
import { getOptionsFromScope } from 'pages/AuditLogs/utilities';
import GroupHeaderContent from './GroupHeaderContent';
import OptionContent from './OptionContent';

const BusinessUnitSelect = ({
  scope,
  handleBusinessEntitySelection,
}: {
  scope: AuditLogsAccessResponse;
  handleBusinessEntitySelection: (selection: FlatOption | null) => void;
}) => {
  const options = useMemo(() => getOptionsFromScope(scope), [scope]);
  const [value, setValue] = useState<FlatOption | null>(null);
  const firstSelectionHappened = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const onChangeHandler = useCallback((_event: SyntheticEvent, selected: FlatOption | null) => {
    firstSelectionHappened.current = true;
    setValue(selected);
  }, []);

  useEffect(() => {
    if (firstSelectionHappened.current) {
      handleBusinessEntitySelection(value);
    }
  }, [value, handleBusinessEntitySelection]);

  const groupBy = (option: FlatOption) => {
    if (option.kind === STANDALONE_KIND) {
      return ''; // No grouping for standalone
    }
    if (option.kind === FIRM_KIND) {
      return option.value; // Group firms by their own value
    }
    // For fund and company, group them under their parent firm
    return option.parentValue || '';
  };

  const renderGroup = (params: any) => {
    const { group, children: groupChildren } = params;

    // Empty group means no grouping (standalone items)
    if (!group) {
      return <div key="no-group">{groupChildren}</div>;
    }

    const groupOption = options.find(opt => opt.value === group);

    // For firm groups, render a sticky header with the firm option
    return (
      <div key={group}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: 'white',
            borderBottom: '1px solid #e0e0e0',
          }}>
          {/* Render the firm option itself as part of the group header */}
          {groupOption && (
            <GroupHeaderContent
              option={groupOption}
              onClick={() => {
                firstSelectionHappened.current = true;
                setValue(groupOption);
                // Blur the input after selection to match regular option behavior
                inputRef.current?.blur();
              }}
            />
          )}
        </Box>
        {groupChildren}
      </div>
    );
  };

  const renderOption = (props: HTMLAttributes<HTMLLIElement>, option: FlatOption) => {
    const kind = option?.kind;
    const isFirm = kind === FIRM_KIND;

    // Skip rendering firm options in the list since they're in the sticky header
    if (isFirm) {
      return null;
    }

    return (
      <li {...props} key={option.value}>
        <OptionContent option={option} paddingLeft={CHILDREN_BUSINESS_UNIT.has(kind) ? '18px' : 0} />
      </li>
    );
  };

  return (
    <Box sx={{ width: 320 }}>
      <Autocomplete<FlatOption, false, false, false>
        options={options}
        value={value}
        onChange={onChangeHandler}
        groupBy={groupBy}
        renderGroup={renderGroup}
        renderOption={renderOption}
        renderInput={params => <TextField {...params} inputRef={inputRef} placeholder="Select a business unit" />}
        isOptionEqualToValue={(option, val) => option.value === val.value}
      />
    </Box>
  );
};

export default BusinessUnitSelect;
